import { Sep } from 'components/common/styled/common-styled';
import { PrimaryButton, SecondaryButton } from 'components/theme/Button';
import useResourceBookingSession from 'features/Reserve/hooks/useResourceBookingSession';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import useContinuousOptions from 'hooks/useContinuousOptions';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AVAILABLE_BRAND_FEATURES } from '@kouto/types';
import { isFeatureActive } from 'utils/featureFlag';
import type { ITax } from 'types/experience.types';
import { ContinuousSessionPicker } from 'components/molecules/ContinuousSessionPicker';
import { isDayBasedTimeFrame } from 'utils/continuousSelection';
import { PriceTierRadioSelector } from '../Booking/PriceTier';
import ResourceAccessCode from './ResourceAccessCode';
import { ResourceMeta } from './ResourceMeta';
import type { StayPricing } from './ResourcePriceTierSelector/NonMappedPriceTierSelector';

interface ResourceDetailContainerProps {
  accessCodeError: string;
  /** Dates with a bookable span on this resource, for a day-based stay's calendar; "YYYY-MM-DD". */
  availableDates?: readonly string[];
  collectionCustomCallToAction: string | null;
  /** The set lengths the group is sold in; when present, the only lengths on offer. */
  durations?: readonly { durationMinutes: number }[];
  isExclusive: boolean;
  isLoadingAvailableDates?: boolean;
  isLoadingCustomQuestions?: boolean;
  onAddToCart: (code: string) => void;
  onCheckout: (code: string) => void;
  onContinue?: (code: string) => void;
  /** The stay calendar has moved months, so the page widens what it knows is available. */
  onMonthChange?: (month: Date) => void;
  /** A start date and a length picked together on the stay calendar. */
  onRangeChange?: (date: string, duration: number) => void;
  onSessionSelect: (time: string | undefined, duration: number) => void;
  onTierSelect: (tierId: string) => void;
  selectedPriceTierId?: string;
  /** What each tier costs for the picked length, as the group page shows it. */
  stayPricing?: StayPricing | null;
}

export const ResourceDetailContainer = ({
  accessCodeError,
  availableDates,
  collectionCustomCallToAction,
  durations,
  isExclusive,
  isLoadingAvailableDates,
  isLoadingCustomQuestions,
  onAddToCart,
  onCheckout,
  onContinue,
  onMonthChange,
  onRangeChange,
  onSessionSelect,
  onTierSelect,
  selectedPriceTierId,
  stayPricing,
}: ResourceDetailContainerProps) => {
  const hasRemoveAddToCart = isFeatureActive(
    AVAILABLE_BRAND_FEATURES.ENABLE_HIDE_ADD_TO_CART_BUTTON,
  );
  const {
    availabilityRule,
    continuousSelection,
    date,
    isContinuous,
    isLoading,
    resource,
    resourceGroup,
    sessions,
    selectedSession,
    timeFrame,
  } = useResourceBookingSession();

  const currency = useBrandCurrency();
  const { t: translate } = useTranslation();

  const { durationOptions, onDurationSelect, onStartSelect, startOptions } =
    useContinuousOptions({
      date,
      durations,
      experienceId: resource?.id,
      onChange: onSessionSelect,
      rule: availabilityRule,
      selection: continuousSelection,
      spans: sessions,
      timeFrame,
    });

  // The picker sends a whole stay here only when no `onRangeChange` came down with it; the start
  // date is already the one on screen, so only the length changes.
  const handleStayChange = (duration: number) =>
    onSessionSelect(undefined, duration);

  if (!currency || !resource || !date) {
    return null;
  }

  // A scheduled resource has nothing to show without a session. A continuously booked one still has
  // its picker: with nothing free on this date, the calendar is how a guest reaches a date that is.
  if (!isContinuous && !selectedSession) {
    return null;
  }

  // Nothing can go in the cart until a start and length are picked.
  const isActionDisabled =
    isLoading || isLoadingCustomQuestions || !selectedSession;

  return (
    <Container className="resource-detail-container">
      <ResourceTitle className="resource-detail-title">
        {resource.title}
      </ResourceTitle>
      <Content className="resource-detail-content">
        {selectedSession && (
          <ResourceMeta
            date={date}
            selectedSession={selectedSession}
            sessions={isContinuous ? [selectedSession] : sessions}
            onSessionSelect={onSessionSelect}
          />
        )}
        {/* The start and end are picked on the group's page; only a stay's dates are changed here. */}
        {isContinuous &&
          availabilityRule &&
          timeFrame &&
          isDayBasedTimeFrame(timeFrame) && (
            <ContinuousSessionPicker
              availableDates={availableDates}
              date={date}
              durationOptions={durationOptions}
              isLoadingAvailableDates={isLoadingAvailableDates}
              onDurationSelect={onDurationSelect}
              onMonthChange={onMonthChange}
              onRangeChange={onRangeChange}
              onStartSelect={onStartSelect}
              onStayChange={handleStayChange}
              rule={availabilityRule}
              selection={continuousSelection}
              startOptions={startOptions}
              timeFrame={timeFrame}
            />
          )}
        <Sep />
        {selectedSession && (
          <PriceTierRadioSelector
            value={selectedPriceTierId}
            priceTiers={selectedSession.priceTiers || []}
            stayPricing={stayPricing}
            taxes={resourceGroup?.experiences?.[0]?.taxes as unknown as ITax[]}
            currency={currency.code || ''}
            onChange={onTierSelect}
          />
        )}
        <Sep />
      </Content>
      <ResourceAccessCodeWrapper isExclusive={isExclusive}>
        <ResourceAccessCode
          errorMessage={accessCodeError}
          fullWidth
          isExclusive={isExclusive}
          renderActionButtons={(accessCode) => {
            return (
              <ActionButtonWrapper className="resource-detail-action-buttons">
                {onContinue && (
                  <PrimaryButton
                    big
                    style={{ width: '100%' }}
                    className="continue-button"
                    disabled={isActionDisabled}
                    onClick={() => onContinue(accessCode)}
                  >
                    {collectionCustomCallToAction || translate('continue')}
                  </PrimaryButton>
                )}
                {!onContinue && (
                  <>
                    {!hasRemoveAddToCart && (
                      <SecondaryButton
                        big
                        disabled={isActionDisabled}
                        onClick={() => onAddToCart(accessCode)}
                        className="add-to-cart-button"
                      >
                        {translate('addToCart')}
                      </SecondaryButton>
                    )}

                    <PrimaryButton
                      big
                      className="checkout-button"
                      disabled={isActionDisabled}
                      onClick={() => onCheckout(accessCode)}
                    >
                      {translate('checkout')}
                    </PrimaryButton>
                  </>
                )}
              </ActionButtonWrapper>
            );
          }}
        />
      </ResourceAccessCodeWrapper>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: min(100%, 846px);
  gap: 8px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 42px;
`;

const ActionButtonWrapper = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-top: 16px;
  width: 100%;
`;

const ResourceTitle = styled.span`
  font-size: 28px;
  line-height: 40px;
  font-weight: 700;
  white-space: normal;
  overflow: hidden;
`;

const ResourceAccessCodeWrapper = styled.div<{ isExclusive: boolean }>`
  margin-top: ${({ isExclusive }) => (isExclusive ? '40px' : '0px')};
`;
